import { Component, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavbarComponent } from './core/navbar/navbar.component';
import { FooterComponent } from './core/footer/footer.component';
import { CookieBannerComponent } from './core/cookie-banner/cookie-banner.component';
import { CookieConsentService, ConsentSettings } from './services/cookie-consent.service';
import { ScrollHandlerService } from './services/scroll-handler.service';
import { filter } from 'rxjs/operators';


declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    doNotTrack?: string;
  }
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NavbarComponent,
    FooterComponent,
    CookieBannerComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'kraeutersaat';
  private isBrowser: boolean;
  private measurementId = 'G-67Z8073YRY';

  constructor(
    private scrollHandlerService: ScrollHandlerService,
    private router: Router,
    private cookieService: CookieConsentService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    // Cookie-Banner-Loading-Klasse
    document.body.classList.add('cookie-consent-loading');
    setTimeout(() => {
      document.body.classList.remove('cookie-consent-loading');
      document.body.classList.add('cookie-consent-checked');
    }, 100);

    // Falls bereits zugestimmt: GA laden
    if (this.cookieService.hasConsented('statistics')) {
      this.loadGA4Script();
    }

    // Auf Consent-Änderungen reagieren
    this.cookieService.consentState$.subscribe((settings: ConsentSettings) => {
      if (settings.statistics) {
        this.loadGA4Script();
      } else {
        this.cookieService.removeGoogleAnalytics();
      }
    });

    // Router-Tracking für SPA
    this.setupRouterTracking();
  }

  public showConsentManager(): void {
    this.cookieService.resetConsent();
    // Hier Banner wieder anzeigen
  }

  private loadGA4Script(): void {
    if (!this.isBrowser) return;
    if (navigator.doNotTrack === '1' || window.doNotTrack === '1') {
      console.log('DNT aktiv -- GA4 wird nicht geladen');
      return;
    }

    // Einmalige Initialisierung
    if (document.querySelector('#ga-script')) {
      return;
    }

    // Skript hinzufügen
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    script.async = true;
    script.id = 'ga-script';
    document.head.appendChild(script);

    // KORRIGIERT: dataLayer und gtag() exakt wie in der Google-Dokumentation
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Consent-Update zuerst
    window.gtag('consent', 'update', {
      analytics_storage: 'granted'
    });

    // GA4 konfigurieren
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
      cookie_expires: 90 * 24 * 60 * 60,
      allow_google_signals: false
    });

    // console.log('GA4 initialisiert');
    
    // Optional: Test-Event senden, um zu verifizieren
    window.gtag('event', 'test_event', {
      event_category: 'debugging',
      event_label: 'script_loaded',
      value: 1
    });
  }

  private setupRouterTracking(): void {
    if (!this.isBrowser) return;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (window.gtag && this.cookieService.hasConsented('statistics')) {
          // Für Single-Page-Apps empfiehlt Google dieses Format
          window.gtag('config', this.measurementId, {
            page_path: event.urlAfterRedirects,
            page_title: document.title,
            page_location: window.location.href
          });
        }
      });
  }
}